import React, { useEffect, useMemo, useState, FC } from 'react';
import './style.scss';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { bookingActions } from 'careand-redux/slices/booking';
import { useAppSelector } from 'careand-redux/store';
import GoogleMap from 'components/Utils/GoogleMap';
import _ from 'lodash';
import DefaultChip from 'components/Utils/DefaultChip';
import { Device } from 'services/Native/Device';
import { ClinicInfo, Patient } from 'generated/api';
import PatientClinicBadge from '../../../../components/PatientClinicBadge';

const google = window.google;

interface SelectClinicProps {
  patient: Patient;
  onSuccess?: (arg: ClinicInfo) => void;
  submitBookingData?: boolean;
  initialClinic?: ClinicInfo;
  allowedClinics?: string[];
  showSelectButton?: boolean;
}

const SelectClinic: FC<SelectClinicProps> = ({
  patient,
  onSuccess,
  submitBookingData = true,
  initialClinic,
  showSelectButton = false,
  allowedClinics,
}) => {
  const [position, setPosition] = useState<GeolocationCoordinates | undefined>(undefined);
  const [travelTime, setTravelTime] = useState<
    Record<string, { text: string; value: number } | undefined>
  >({});

  const dispatch = useAppDispatch();
  const physicians = useAppSelector((state) => state.physicians.byId);
  const allClinics = useAppSelector((state) => state.resource.clinics);
  const booking = useAppSelector((state) => state.booking);
  const permission = useAppSelector((state) => state.device.permissions['location']);

  useEffect(() => {
    if (Device.isMobile()) {
      Device.requestPermission('location');
    }
  }, [permission]);

  //get location from browser
  useEffect(() => {
    if (permission !== false) {
      navigator.geolocation.getCurrentPosition((position) => {
        if (position && position.coords) {
          setPosition(position.coords);
        }
      });
    }
  }, [permission]);

  const defaultClinicId =
    initialClinic?.id || booking?.appointment?.clinic_id || patient.clinic_id || allClinics[0]!.id;

  const [clinicId, setClinicId] = useState(defaultClinicId);

  // Base clinics list (without travel time sorting)
  const baseClinics = useMemo(() => {
    let result = allClinics.filter(({ hide }) => !hide);

    // Filter by allowed clinics if specified
    if ((allowedClinics?.length || 0) > 0) {
      result = result.filter((clinic) => allowedClinics?.includes(clinic.id!));
    }

    return result;
  }, [allClinics, allowedClinics]);

  // Sorted clinics list (includes travel time sorting)
  const availableClinics = useMemo(() => {
    let result = [...baseClinics];

    result = _.orderBy(
      result,
      (clinic) => {
        return travelTime[clinic.id!]?.value || 0;
      },
      ['asc'],
    );
    let own = result.find(({ id }) => id === defaultClinicId);
    if (own) result = [own, ...result];
    return _.uniqBy(result, 'id');
  }, [baseClinics, defaultClinicId, travelTime]);

  useEffect(() => {
    if (position) {
      baseClinics.forEach((clinic) => {
        let address = clinic.address;
        if (!!address?.latitude && !!address?.longitude) {
          let origin = new google.maps.LatLng({ lat: position.latitude, lng: position.longitude });
          let destination = new google.maps.LatLng({
            lat: parseFloat(address.latitude),
            lng: parseFloat(address.longitude),
          });
          let service = new google.maps.DistanceMatrixService();
          service.getDistanceMatrix(
            {
              origins: [origin],
              destinations: [destination],
              travelMode: google.maps.TravelMode.DRIVING,
              unitSystem: google.maps.UnitSystem.METRIC,
              avoidHighways: false,
              avoidTolls: false,
            },
            (response: any, status: string) => {
              if (status === google.maps.DistanceMatrixStatus.OK) {
                setTravelTime((prev) => {
                  return {
                    ...prev,
                    [clinic.id!]: response?.rows[0].elements[0]?.duration,
                  };
                });
              }
            },
          );
        }
      });
    }
  }, [baseClinics, position]);

  function onSelectedClinic(clinic: ClinicInfo) {
    if (submitBookingData) {
      const selectPrimary =
        patient.primary_physician && physicians[patient.primary_physician]?.clinic_id === clinic.id;
      dispatch(
        bookingActions.clinicAndPractitionerChanged({
          clinicId: clinic.id!,
          physicianId: selectPrimary ? patient.primary_physician : undefined,
        }),
      );
    }
    if (onSuccess) onSuccess(clinic);
  }

  function onClickedClinic(clinic: ClinicInfo) {
    if (clinic.id === clinicId) {
      onSelectedClinic(clinic);
    }
    setClinicId(clinic.id);
  }

  const latLngs = useMemo(() => {
    return _.compact(
      availableClinics.map((clinic) => {
        let address = clinic.address;
        if (!!address?.latitude && !!address?.longitude) {
          return new google.maps.LatLng({
            lat: parseFloat(address.latitude),
            lng: parseFloat(address.longitude),
          });
        }
      }),
    );
  }, [availableClinics]);

  const center = useMemo(() => {
    let clinic = availableClinics[0]!;
    let address = clinic.address;
    if (!!address?.latitude && !!address?.longitude) {
      let lat = parseFloat(address.latitude);
      let lng = parseFloat(address.longitude);
      return new google.maps.LatLng({ lat, lng });
    }
  }, [availableClinics]);

  const panTo = useMemo(() => {
    let clinic = availableClinics.find(({ id }) => id === clinicId);
    let address = clinic?.address;
    if (!!address?.latitude && !!address?.longitude) {
      let lat = parseFloat(address.latitude);
      let lng = parseFloat(address.longitude);
      return new google.maps.LatLng({ lat, lng });
    }
  }, [clinicId, availableClinics]);

  const currentPosition = useMemo(() => {
    if (position) {
      return new google.maps.LatLng({ lat: position.latitude, lng: position.longitude });
    }
  }, [position]);

  return (
    <div className="choose-clinic">
      <GoogleMap
        currentPosition={currentPosition}
        latLngs={latLngs}
        center={center}
        panTo={panTo}
        zoom={13}
      />
      <div className="clinic-cards">
        <div className="cards-wrapper">
          {availableClinics.map((clinic) => {
            let availability = (clinic.availability || '').toLowerCase();
            let minutes = travelTime[clinic.id!]?.text;

            return (
              <PatientClinicBadge
                clinic={clinic}
                key={clinic.id}
                onClick={onClickedClinic}
                selected={clinicId === clinic.id}
                extraInfo={
                  (availability || minutes) && (
                    <div className="misc">
                      {availability && (
                        <span className={availability + ' bold capitalize'}>
                          {availability} availability
                        </span>
                      )}
                      {minutes && (
                        <>
                          ○<span>{minutes} away</span>
                        </>
                      )}
                    </div>
                  )
                }
              >
                {(submitBookingData || showSelectButton) && (
                  <DefaultChip
                    className="button"
                    disabled={clinicId !== clinic.id}
                    text="Select"
                    onClick={() => onSelectedClinic(clinic)}
                  />
                )}
              </PatientClinicBadge>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SelectClinic;
